import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { OBAttempt } from "src/app/models/models";
import { ObRoleSlot, ObEventForResetFilter, parseObsCode } from "../../shared-v2/utils/observation.utils";
import { buildObservationCode } from "./ts1-rating-ob-interaction";
import { includeTs1V3ObInRatingNewObs } from "./ts1-rating-ab-ob-merge.utils";
import { TS1_REVIEW_COMPETENCY_ORDER } from "./ts1-rating-review.utils";

const MANEUVER_META_KEYS = new Set([
	"formType",
	"maneuver",
	"maneuverCode",
	"assessmentId",
	"traineeAssessmentId",
	"candidateId",
	"selectedOBs",
	"safetyOutcome",
	"repeat",
	"candidateRole",
	"otherComents",
]);

function formGroupControls(group: AbstractControl | null): Record<string, AbstractControl> | null {
	if (!(group instanceof UntypedFormGroup) || !group.controls) return null;
	return group.controls;
}

/** OB has value when active attempts remain and the role is not N/A. */
function hasObValue(value: OBAttempt): boolean {
	return (value.currentCount ?? 0) > 0 && !value.isDisabled;
}

function isApplicableOb(value: OBAttempt): boolean {
	if (value.isDisabled) return false;
	return value.preDefined === true || (value.totalCount ?? 0) > 0;
}

/** Role slot contributes when it has value or is a non-N/A applicable OB. */
function roleSlotContributes(value: OBAttempt): boolean {
	if (value.isDisabled) return false;
	if (hasObValue(value)) return true;
	return isApplicableOb(value);
}

function readRoleObValue(obCell: AbstractControl | null, roleSlot: ObRoleSlot): OBAttempt | null {
	if (!obCell) return null;
	const hasRoleSlots = typeof obCell.get === "function" && (obCell.get("pf") || obCell.get("pm"));
	if (hasRoleSlots) {
		const ctrl = obCell.get(roleSlot);
		return (ctrl?.value ?? null) as OBAttempt | null;
	}
	return (obCell.value ?? null) as OBAttempt | null;
}

function observationCodeFromCellKey(catKey: string, obKey: string): string {
	const parsed = parseObsCode(obKey);
	if (parsed) {
		return buildObservationCode(`${parsed.cat} ${parsed.num}`);
	}
	const numMatch = /(\d+)/.exec(obKey);
	if (numMatch) {
		return buildObservationCode(`${catKey} ${numMatch[1]}`);
	}
	return buildObservationCode(`${catKey}_${obKey}`);
}

const OB_ROLE_SLOTS: ObRoleSlot[] = ["pf", "pm"];

/**
 * Count unique applicable OB codes per competency for the carousel candidate.
 * Checks both PF and PM columns (union) so single-candidate dual-column sessions activate correctly.
 *
 * Applicable when either role still has value (currentCount > 0, not N/A) or when
 * an untouched predefined/activated OB remains selected on a non-N/A role.
 */
export function buildTs1CompetencyApplicableCounts(params: {
	ratingForm: UntypedFormArray;
	candidateIndex: number;
}): Map<string, number> {
	const { ratingForm, candidateIndex } = params;
	const uniqueByCompetency = new Map<string, Set<string>>();

	if (!ratingForm?.length) return new Map();

	const observations = ratingForm.at(candidateIndex)?.get("observations") as UntypedFormArray | null;
	if (!observations) return new Map();

	for (let m = 0; m < observations.length; m++) {
		const obsControls = formGroupControls(observations.at(m));
		if (!obsControls) continue;

		for (const catKey of Object.keys(obsControls)) {
			if (MANEUVER_META_KEYS.has(catKey)) continue;

			const catControls = formGroupControls(obsControls[catKey]);
			if (!catControls) continue;

			const competency = catKey.toUpperCase();
			for (const cellKey of Object.keys(catControls)) {
				const obCell = catControls[cellKey];
				const obsCode = observationCodeFromCellKey(catKey, cellKey);

				for (const roleSlot of OB_ROLE_SLOTS) {
					const value = readRoleObValue(obCell, roleSlot);
					if (!value || !roleSlotContributes(value)) continue;

					if (!uniqueByCompetency.has(competency)) {
						uniqueByCompetency.set(competency, new Set());
					}
					uniqueByCompetency.get(competency)!.add(obsCode);
				}
			}
		}
	}

	const counts = new Map<string, number>();
	for (const [competency, codes] of uniqueByCompetency) {
		counts.set(competency, codes.size);
	}
	return counts;
}

function competencyCodeFromObservationCode(code: string): string | null {
	const normalized = code.trim().toUpperCase();
	const match = /^([A-Z]+)/.exec(normalized);
	return match?.[1] ?? null;
}

/**
 * Count applicable competencies from add/remove OB events (any PF/PM column).
 * Used by staging/checklist preview where live ratingForm is unavailable.
 * N/A roles never contribute; removed 0/N unobserve still contributes for applicable
 * (How Often needs those rows). Zero-active non-removed roles do not.
 */
export function buildTs1ApplicableCountsFromAddRemoveOb(
	events: ObEventForResetFilter[],
): Map<string, number> {
	const uniqueByCompetency = new Map<string, Set<string>>();

	for (const event of events) {
		if (!includeTs1V3ObInRatingNewObs(event)) continue;

		const obsCode = (event.observationCode ?? "").trim();
		if (!obsCode) continue;

		const competency = competencyCodeFromObservationCode(obsCode);
		if (!competency) continue;

		if (!uniqueByCompetency.has(competency)) {
			uniqueByCompetency.set(competency, new Set());
		}
		uniqueByCompetency.get(competency)!.add(obsCode);
	}

	const counts = new Map<string, number>();
	for (const code of TS1_REVIEW_COMPETENCY_ORDER) {
		counts.set(code, uniqueByCompetency.get(code)?.size ?? 0);
	}
	for (const [competency, codes] of uniqueByCompetency) {
		if (!counts.has(competency)) {
			counts.set(competency, codes.size);
		}
	}
	return counts;
}
